import { useEffect, useState } from 'react';
import { Box, Text, Flex, Card, Badge, SegmentedControl, Dialog, VisuallyHidden, Inset, Progress } from '@radix-ui/themes';
import CircleProgress from '../../shared/CircleProgress';
import LoadingIndicator from '../../shared/LoadingIndicator';
import { reportService } from '../../../services/teacher/reportService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronRightIcon, CircleBackslashIcon, DotFilledIcon } from '@radix-ui/react-icons';
import TeacherQuizReport from './TeacherQuizReport';
import MessageBox from '../../common/MessageBox';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CustomLineChart from '../../common/CustomLineChart';
import { COMPETENCY_COLORS } from '../../../utils/constants';

const CourseReport = ({ course_id }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [courseDetails, setCourseDetails] = useState(null);
    const [activePlanReport, setActivePlanReport] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [selectedReportView, setSelectedReportView] = useState('first_attempt');
    const [selectedChartView, setSelectedChartView] = useState('scores');
    const [selectedQuizReport, setSelectedQuizReport] = useState(null);

    useEffect(() => {
        const fetchReport = async () => {
            setLoading(true);
            try {
                setError(null);
                const data = await reportService.getCourseReport(course_id);
                console.log('course report data>>>>>', data);
                if (data.error) {
                    setError(data.error);
                } else {
                    setCourseDetails(data.course_report.course_details);
                    setAttempts(data.course_report.attempts);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        // fetchReport();
        fetchActivePlanReport();
    }, [course_id]);

    const fetchActivePlanReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await reportService.getCourseReport(course_id);
            console.log('course report data>>>>>', data);
            if (data.error) {
                setError(data.error);
            } else {
                // setCourseDetails(data.course_report.course_details);
                // setAttempts(data.course_report.attempts);
                console.log('course details>>>>>', data);
                setActivePlanReport(data.course_report);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    const calculateSummary = () => {
        if (!activePlanReport || !activePlanReport.quizzes.length) return { avgScore: 0, minScore: 0, maxScore: 0, totalEngaged: 0 };

        activePlanReport.quizzes.map((quiz) => {
            quiz.quiz_attempts.map((attempt) => {
                if (attempt.vocabulary_score) {
                    attempt.vocabulary_score = parseFloat(attempt.vocabulary_score);
                }
                }
            )
        })
    
        const quizzesSummary = activePlanReport.quizzes.reduce((acc, curr) => {
            return {
                avgScore: acc.avgScore + parseFloat(curr.avg_percentage),
                minScore: Math.min(acc.minScore, curr.min_percentage),
                maxScore: Math.max(acc.maxScore, curr.max_percentage),
                totalEngaged: Math.max(acc.totalEngaged, curr.total_students_engaged)
            };
        }, { avgScore: 0, minScore: 100, maxScore: 0, totalEngaged: 0 });

        summary.avgScore = summary.avgScore / attempts.length;
        return quizzesSummary;
    };


    const createChartData = () => {
        //check the number of entries to display
        return activePlanReport.quizzes.map((quiz, index) => {
            const performance = activePlanReport.performances.find(p => p.quiz_id === quiz.id);
            if (!performance) return {
                quizNumber: `Q${index + 1}`,
                averageScore: null,
                minScore: null,
                maxScore: null
            };
            return {
            quizNumber: `Q${index + 1}`,
            averageScore: parseFloat(performance.average_score || 0),
            minScore: performance.min_score,
            maxScore: performance.max_score
            }
        });
    };

    const createTimeChartData = () => {
        return activePlanReport.quizzes.map((quiz, index) => {
            const performance = activePlanReport.performances.find(p => p.quiz_id === quiz.id);
            if (!performance) return {
                quizNumber: `Q${index + 1}`,
                expectedDuration:quiz.expected_duration,
                averageDuration: null,
                minDuration: null,
                maxDuration: null
            };
            return {
            quizNumber: `Q${index + 1}`,
            expectedDuration:quiz.time_limit,
            averageDuration: parseFloat(performance.average_duration || 0),
            minDuration: performance.min_duration,
            maxDuration: performance.max_duration
            }
        });
    }

    const createCompetencyChartData = () => {
        return activePlanReport.quizzes.map((quiz, index) => {
            console.log('quiz>>>>>', quiz);
            return {
            quizNumber: `Q${index + 1}`,
            average_vocabulary_score: quiz.quiz_attempts ? Math.round(quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.vocabulary_score || 0), 0) / quiz.quiz_attempts.length) : 0,
            average_concept_score: quiz.quiz_attempts ? quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.concept_score || 0), 0) / quiz.quiz_attempts.length : 0,
            average_application_score: quiz.quiz_attempts ? quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.application_score || 0), 0) / quiz.quiz_attempts.length : 0
            }
        });
    }

    if (loading) {
        return (
            <Flex align="center" justify="center" p="4">
                <LoadingIndicator />
            </Flex>
        );
    }

    if (error) {
        return (
            <Flex align="center" justify="center" p="4">
                <Text color="red">Error: {error}</Text>
            </Flex>
        );
    }

    const handleViewQuizReport = async (quizId) => {
        console.log('quizId>>>>>', quizId);
        setSelectedQuizReport(quizId);
    }

    // const summary = calculateSummary();

    return (
        <Box>
            {activePlanReport.performances.length > 1 && (
                <>
                <SegmentedControl.Root size="1" my="4" defaultValue="scores" value={selectedChartView} onValueChange={(value) => setSelectedChartView(value)} style={{ width: '300px' }}>
                    <SegmentedControl.Item value="scores">Scores</SegmentedControl.Item>
                    <SegmentedControl.Item value="timing">Timing</SegmentedControl.Item>
                    <SegmentedControl.Item value="competency">Competency</SegmentedControl.Item>
                </SegmentedControl.Root>

                    {selectedChartView === 'scores' && (
                        // <Box style={{ width: '100%', height: '300px' }}>
                            <Inset style={{height: '300px' }}>
                        <CustomLineChart
                            data={createChartData()}
                            lines={[
                                { dataKey: "averageScore", stroke: "#8884d8", name: "Average" },
                                { dataKey: "maxScore", stroke: "#82ca9d", name: "Max", strokeDasharray: "4 2" },
                                { dataKey: "minScore", stroke: "#ff7300", name: "Min", strokeDasharray: "4 2" },
                            ]}
                            xAxisKey="quizNumber"
                            yAxisDomain={[0, 100]}
                            />
                            </Inset>
                        // </Box>
                    )}

                    {selectedChartView === 'timing' && (
                        <Box style={{ width: '100%', height: '300px' }}>
                        <CustomLineChart
                            data={createTimeChartData()}
                            lines={[
                                { dataKey: "expectedDuration", stroke: "#888811", name: "Expected" },
                                { dataKey: "averageDuration", stroke: "#8884d8", name: "Average" },
                                { dataKey: "maxDuration", stroke: "#82ca9d", name: "Max", strokeDasharray: "4 2" },
                                { dataKey: "minDuration", stroke: "#ff7300", name: "Min", strokeDasharray: "4 2" },
                            ]}
                            xAxisKey="quizNumber"
                            yAxisDomain={[0, 100]}
                            />
                        </Box>
                    )}

                    {selectedChartView === 'competency' && (
                        <Box style={{ width: '100%', height: '300px' }}>
                        <CustomLineChart
                            data={createCompetencyChartData()}
                            lines={[
                                { dataKey: "average_vocabulary_score", stroke: "#888811", name: "Vocabulary" },
                                { dataKey: "average_concept_score", stroke: "#8884d8", name: "Concept" },
                                { dataKey: "average_application_score", stroke: "#82ca9d", name: "Application" }
                            ]}
                            xAxisKey="quizNumber"
                            yAxisDomain={[0, 100]}
                            />
                        </Box>
                    )}
                </>
            )}

            <Box p="3" mt="4">
                {/* <Text size="3" weight="bold" mb="3">Quiz Details</Text> */}
                <Flex direction="column" gap="3">
                    {activePlanReport.quizzes.length ? (
                        activePlanReport.quizzes.map((quiz, index) => {
                            if(quiz.quiz_attempts.length > 0 ){
                            const vocabularyAvg = quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.vocabulary_score || 0), 0) / quiz.quiz_attempts.length;
                            const conceptAvg = quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.concept_score || 0), 0) / quiz.quiz_attempts.length;
                            const applicationAvg = quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.application_score || 0), 0) / quiz.quiz_attempts.length;
                            const scoreAvg = quiz.quiz_attempts.reduce((acc, attempt) => acc + (attempt.percentage || 0), 0) / quiz.quiz_attempts.length;

                            return (
                                <Flex key={quiz.id} className="flatList cursor-pointer" direction="row" align="center" justify="between" gap="2" p="2" onClick={() => handleViewQuizReport(quiz.id)}>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" as='p'>Quiz #{index + 1} | {quiz.lesson.title}</Text>
                                        <Flex direction="row" gap="2" align="end">
                                            <Flex direction="column" gap="1" p="1" maxWidth="80px">
                                                <Text size="1" color="gray">Voc: <Text weight="bold">{Math.floor(vocabularyAvg)}%</Text></Text>
                                                <Progress value={vocabularyAvg} size="2" color={COMPETENCY_COLORS.vocabulary}/>
                                            </Flex>
                                            <Flex direction="column" gap="1" p="1" maxWidth="80px">
                                                <Text size="1" color="gray">Con: <Text weight="bold">{Math.floor(conceptAvg)}%</Text></Text>
                                                <Progress value={conceptAvg} size="2" color={COMPETENCY_COLORS.concept}/>
                                            </Flex>
                                            <Flex direction="column" gap="1" p="1" maxWidth="80px">
                                                <Text size="1" color="gray">App: <Text weight="bold">{Math.floor(applicationAvg)}%</Text></Text>
                                                <Progress value={applicationAvg} size="2" color={COMPETENCY_COLORS.application}/>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                    <CircleProgress
                                        progress={Math.floor(scoreAvg)}
                                        label={`${Math.floor(scoreAvg)}%`}
                                        size={60}
                                        color={'#00b341'}
                                    />
                                </Flex>
                            
                            )
                        } else {
                            return (
                                <Flex key={quiz.id} className="flatList" direction="column" align="start" justify="start" gap="2" p="2">
                                    <Text size="2" as='p'>Quiz #{index + 1} | {quiz.lesson.title}</Text>
                                    <Text size="1" as='p' weight="bold">No attempts available</Text>
                                </Flex>
                            )
                        }

                        })
                    ) : (
                        <Flex direction="row" align="start" justify="between" gap="2" p="2">
                            <MessageBox type="warning" message="No quizzes available" />
                        </Flex>
                    )}
                </Flex>
            </Box>

            {/* <AnimatePresence>
                {selectedQuizReport && (
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.5 }}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', paddingTop: 50 }}>
                        <div style={{ backgroundColor: 'var(--gray-1)', padding: '10px', borderRadius: '10px' }}>
                            <TeacherQuizReport quizId={selectedQuizReport} onClose={() => setSelectedQuizReport(null)} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence> */}


            <Dialog.Root open={selectedQuizReport != null} onOpenChange={(boolean) => boolean ? null : setSelectedQuizReport(null)}>
                <Dialog.Content>
                    <Dialog.Title>
                        <VisuallyHidden><Text>Quiz Report</Text></VisuallyHidden>
                    </Dialog.Title>
                    <Dialog.Description>
                        <VisuallyHidden><Text>Quiz Report</Text></VisuallyHidden>
                    </Dialog.Description>
                    <TeacherQuizReport quizId={selectedQuizReport} onClose={() => setSelectedQuizReport(null)} />
                </Dialog.Content>
            </Dialog.Root>
        </Box >
    );
};

export default CourseReport;
